// World-door traversal handoff (DOR-2, M41). A door traversal is a world-session swap:
// the game flushes, then navigates to `?world=<targetId>` so the browser tears the whole
// session down (guaranteed clean — no leaked meshes/workers/listeners), and boot re-enters
// `startGame` for the target world. The one thing that must survive the navigation is the
// player's carried inventory + condition (positions/spawn are per-world, DOR-2), so it
// rides in sessionStorage — read once on the far side and cleared, so a manual reload
// afterwards doesn't re-apply it.
import type { PlayerSave } from '../../sim/game/player';

const KEY = 'bb-door-handoff';

export interface DoorHandoff {
  /** The world being entered (matches the `?world=` param on the far side). */
  targetWorldId: string;
  /** The paired door to arrive at (its `doorId`); '' ⇒ arrive at the world spawn. */
  targetDoorId: string;
  /** The player's carried survival state — inventory + vitals (DOR-2). */
  carry: PlayerSave;
  /** The world traversed from (provenance / debugging). */
  fromWorldId: string;
}

/** Stash a traversal handoff just before navigating to the target world. */
export function writeHandoff(h: DoorHandoff): void {
  try {
    sessionStorage.setItem(KEY, JSON.stringify(h));
  } catch {
    /* private mode / disabled storage → the target simply starts with its own inventory */
  }
}

/** Read + CLEAR the pending handoff (single-use, so a later manual reload is clean). */
export function takeHandoff(): DoorHandoff | null {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    sessionStorage.removeItem(KEY);
    return JSON.parse(raw) as DoorHandoff;
  } catch {
    return null;
  }
}
